import { Box, ButtonBase, CircularProgress, Collapse, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSingleImportMutation } from "../../../newapi/global/uploadApiSlice";

import { ReactComponent as Shirt } from '../../../styles/svg/shirt.svg';
import { ReactComponent as Crate } from '../../../styles/svg/crate.svg';
import { ReactComponent as Excel } from '../../../styles/svg/excel.svg';
import { ReactComponent as Border } from '../../../styles/svg/border.svg';
import Heading from "../text/Heading";
import Subheading from "../text/Subheading";
import Text from "../text/Text";
import MConfirmation from "../../global/Modals/MConfirmation";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { InputDropdown } from "../dropdowns/InputDropdown";
import SettingsSaveButton from "../../global/Settings/SettingsSaveButton";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import cx from "classnames";

export const ImportStandAloneFile = (props) => {
    const { t, ready } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // const [source,setSource] = useState(props.source ? props.source : "monta");
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadType, setUploadType] = useState(props.type);

    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const [uploadSingleFile,{isLoading:uploadFileLoading}] = useSingleImportMutation();

    // Settings a caller renders above the drop zone. A field is one of:
    //   { name, label, type: "list", options: [{id, name}], defaultValue }
    //   { name, label, type: "boolean", defaultValue }
    //   { name, label, type: "custom", defaultValue, render: ({ value, onChange, values }) => node }
    // and may carry onChange(value, values), for a caller that rebuilds the other fields on a
    // pick. Defaults that change after mount, such as stored options fetched from the API,
    // reseed the values. A caller that keeps the settings passes onSaveSettings(values) and
    // gets a save button on the first row, enabled once the values differ from the defaults.
    const hasImportSettings = Array.isArray(props.importSettings) && props.importSettings.length > 0;
    const buildInitialQueryParams = () => hasImportSettings
        ? props.importSettings.reduce((acc, field) => {
            acc[field.name] = field.defaultValue !== undefined
                ? field.defaultValue
                : field.type === "boolean" ? false : "";
            return acc;
        }, {})
        : {};
    const [queryParams, setQueryParams] = useState(buildInitialQueryParams);
    const [settingsExpanded, setSettingsExpanded] = useState(true);

    const defaultsKey = JSON.stringify((props.importSettings ?? []).map(field => [field.name, field.defaultValue]));
    useEffect(() => {
        if (hasImportSettings) {
            setQueryParams(buildInitialQueryParams());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultsKey]);

    const updateQueryParam = (name, value) => {
        setQueryParams(prev => ({ ...prev, [name]: value }));
        props.importSettings?.find(field => field.name === name)?.onChange?.(value, queryParams);
    };

    const hasSettingsChanges = hasImportSettings && JSON.stringify(queryParams) !== JSON.stringify(buildInitialQueryParams());
    const [savingSettings, setSavingSettings] = useState(false);

    const saveSettings = async (e) => {
        e.stopPropagation();
        if (!props.onSaveSettings || savingSettings) return;
        setSavingSettings(true);
        try {
            await props.onSaveSettings(queryParams);
        } finally {
            setSavingSettings(false);
        }
    };

    const settingsSaveButton = props.onSaveSettings
        ? <SettingsSaveButton onClick={saveSettings} isLoading={savingSettings} disabled={!hasSettingsChanges} />
        : null;

    useEffect(() => {
        if (!hasImportSettings) return;
        if (fileInputRef.current) {
            fileInputRef.current.dataset.queryParams = JSON.stringify(queryParams);
        }
    }, [queryParams, hasImportSettings]);

    const [confirmEndStreamModalOpen, setConfirmEndStreamModalOpen] = useState(false);
    const confirmEndStreamModal = {
        open: confirmEndStreamModalOpen,
        setOpen: setConfirmEndStreamModalOpen,
    };

    const handleConfirmEndStreamClose = () => {
        confirmEndStreamModal.setOpen(false);
    };

    // Function to simulate click on file input
    const handleClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle drag events
    const handleDrag = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.type === 'dragenter' || event.type === 'dragover') {
            setDragActive(true);
        } else if (event.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    // Handle drop event
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            // Handle the file...
            setSelectedFile(event.dataTransfer.files[0]);
            // console.log(event.dataTransfer.files[0]);
        }
    }, []);

    // Handle file selection via input
    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            // Handle the file...
            setSelectedFile(event.target.files[0]);
            // console.log(event.target.files[0]);
        }
    };

    const breakpoint = props.breakpoint ?? "phone";

    const getBreakPoint = () => {
        if(breakpoint == "phone") return 768;
        if(breakpoint == "tablet") return 1024;
        if(breakpoint == "smallpc") return 1366;
    }

    const [uploadResults, setUploadResults] = useState(null);
    const handleUpload = async (event) => {
        if(uploadFileLoading) return;
        if(event != null) {
            event.stopPropagation();
        }
        setUploadResults(null);

        // Settings rendered by this modal are read straight from state. Only a caller that
        // sets the params on the input from outside — the settings import page clicks it with
        // its own choices already attached — needs them read back off the dataset, and those
        // are gone after the first upload because the finally below clears them.
        let parsedQueryParams = {};
        if (hasImportSettings) {
            parsedQueryParams = queryParams;
        } else if (fileInputRef.current?.dataset.queryParams) {
            try {
                parsedQueryParams = JSON.parse(fileInputRef.current.dataset.queryParams);
            } catch (e) {
                console.error('Error parsing query params:', e);
            }
        }

        if (selectedFile) {
            try {
                const response = await uploadSingleFile({
                    type: props.fileType,
                    file: selectedFile,
                    ...parsedQueryParams
                }).unwrap();


                if (response) {

                    setUploadResults(response);
                    const noneHaveNotImported = response.results.every(x => x.notImported.length === 0);

                    if(response.errors.length === 0 && noneHaveNotImported) {

                        if(props.handleClose ){
                            showToastMessagePostRequest(t,response);
                        }
                    }

                }
            } catch (error) {
                console.error('Error uploading file:', error);
                showToastMessageRequestError(t("upload_failed"),t,error);
            } finally {
                if (fileInputRef.current) {
                    delete fileInputRef.current.dataset.queryParams;
                }
            }
        }
    };
    return (
        <Box
            sx={{
                maxHeight:800,
                borderRadius:6,
                overflow:"hidden",
                display:"flex",
            }}
        >
            <ButtonBase
                component="div"
                onDragEnd={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
                sx={{
                    flex:1,
                    borderRadius:4,
                    px:1.5,
                    pt:4,
                    pb:1.5,
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[100],
                    display:"flex",
                    alignItems:"center",
                    flexDirection:'column',
                    minHeight:0,
                    ".colored" : {
                        // color: "rgb(65,128,246)",
                        color: colors[theme.palette.mode === "dark" ? "blue" : "primary"][400]
                    },
                    "& .border" : {
                        position:"absolute",
                        top:0,
                        left:0,
                        height:1,
                        width:1,
                        borderRadius:3,
                        pointerEvents:"none",

                        "& path" : {
                            fill: "none",
                            stroke: "transparent",
                            // stroke: "rgb(65,128,246)",
                            strokeWidth:4,
                            strokeDasharray:"10,10",
                            animation: "dash 25s linear infinite",
                            animationPlayState: "paused"
                        },
                        
                    },
                    "&:hover .border path" : {
                        animationPlayState: "running",
                        stroke:colors[theme.palette.mode === "dark" ? "blue" : "primary"][400],
                    },
                }}
            >

                <Border className='border'/>
                <input
                    type="file"
                    id="file-upload"
                    ref={fileInputRef} // Attach the ref to the file input
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />

                {uploadFileLoading ? (
                    <Box
                        sx={{
                            display:"flex",
                            flexDirection:"column",
                            alignItems:"center",
                            gap:3,
                            py:6,
                        }}
                    >
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                            }}
                        />
                        <Text center light>{t("uploading_file")}</Text>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                width:1,
                                flexShrink:0,
                                display:"flex",
                                flexDirection:"column",
                                alignItems:"center",
                            }}
                        >
                            {props.logo}
                            <Subheading
                                sx={{
                                    mt:1.5,
                                }}
                            >
                                {props.title}
                            </Subheading>
                            <Box>
                                <Trans
                                    i18nKey="uploadFileText"
                                    values={{ clickHere: t('clickHereText') }}
                                    components={{
                                        strong: <strong className="colored" />
                                    }}
                                />
                            </Box>
                            {hasImportSettings && (
                                <ButtonBase
                                    component="div"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSettingsExpanded(prev => !prev);
                                    }}
                                    sx={{
                                        mt:3,
                                        width:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                        borderRadius:4,
                                        p:1.5,
                                        display:"flex",
                                        flexDirection:"column",
                                        alignItems:"stretch",
                                        textAlign:"left",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:1,
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"space-between",
                                            gap:1,
                                        }}
                                    >
                                        <Box sx={{display:"flex", alignItems:"center", gap:1}}>
                                            <SettingsOutlinedIcon fontSize="small" />
                                            <Text bold>{t("import_settings")}</Text>
                                        </Box>
                                        <ExpandMoreIcon
                                            sx={{
                                                transition:"transform 200ms",
                                                transform: settingsExpanded ? "rotate(180deg)" : "rotate(0deg)"
                                            }}
                                        />
                                    </Box>
                                    <Collapse in={settingsExpanded} unmountOnExit sx={{width:1}}>
                                        {/* Bounded and scrollable: a list setting can run to many rows, and the
                                            file row and upload button below must stay in reach. */}
                                        <Box
                                            sx={{
                                                mt:1.5,
                                                display:"flex",
                                                flexDirection:"column",
                                                gap:1.5,
                                                maxHeight:360,
                                                overflowY:"auto",
                                                pr:0.5,
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {props.importSettings.map((field, index) => {
                                                const value = queryParams[field.name];
                                                // The save button sits beside the first field, where it is seen first.
                                                const rowSaveButton = index === 0 ? settingsSaveButton : null;

                                                if (field.type === "list") {
                                                    const options = field.options ?? [];
                                                    const selectedIdx = options.findIndex(o => o.id === value);
                                                    return (
                                                        <Box
                                                            key={field.name}
                                                            sx={{
                                                                width:1,
                                                                display:"flex",
                                                                alignItems:"center",
                                                                justifyContent:"space-between",
                                                                gap:2,
                                                            }}
                                                        >
                                                            <Text light>{field.label}</Text>
                                                            <Box sx={{flex:1, display:"flex"}}>
                                                                <InputDropdown
                                                                    name="name"
                                                                    displayName="name"
                                                                    selected={selectedIdx}
                                                                    options={options}
                                                                    noSelection={t("choose_option")}
                                                                    onChange={(idx) => {
                                                                        updateQueryParam(field.name, options[idx]?.id);
                                                                    }}
                                                                    closeOnSelection={true}
                                                                />
                                                            </Box>
                                                            {rowSaveButton}
                                                        </Box>
                                                    );
                                                }

                                                if (field.type === "boolean") {
                                                    return (
                                                        <Box
                                                            key={field.name}
                                                            sx={{
                                                                width:1,
                                                                display:"flex",
                                                                alignItems:"center",
                                                                justifyContent:"space-between",
                                                                gap:2,
                                                            }}
                                                        >
                                                            <Text light>{field.label}</Text>
                                                            <Box sx={{display:"flex", alignItems:"center", gap:2}}>
                                                            <ToggleButtonGroup
                                                                value={value === true ? "true" : "false"}
                                                                exclusive
                                                                onChange={(e, newVal) => {
                                                                    e.stopPropagation();
                                                                    if (newVal === null) return;
                                                                    updateQueryParam(field.name, newVal === "true");
                                                                }}
                                                                aria-label={field.name}
                                                                className={cx(`${value}`, {
                                                                    'active': value === true
                                                                })}
                                                                sx={{
                                                                    background: colors.red[100],
                                                                    padding: 0.5,
                                                                    borderRadius: 5,
                                                                    transition: "250ms",
                                                                    "&.active": {
                                                                        background: colors.green[100],
                                                                        transition: "250ms",
                                                                    },
                                                                }}
                                                            >
                                                                <ToggleButton
                                                                    className={cx('', { 'active': value !== true })}
                                                                    sx={{
                                                                        padding:0,
                                                                        paddingX:2,
                                                                        border:0,
                                                                        lineHeight:"28px",
                                                                        textTransform:"unset",
                                                                        borderRadius:"32px !important",
                                                                        color: colors.green[400],
                                                                        transition: "250ms",
                                                                        "&.active, &.Mui-selected, &.Mui-selected:hover": {
                                                                            background: colors.red[400],
                                                                            color: theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff",
                                                                        }
                                                                    }}
                                                                    value="false"
                                                                >
                                                                    {t('no')}
                                                                </ToggleButton>
                                                                <ToggleButton
                                                                    className={cx('', { 'active': value === true })}
                                                                    sx={{
                                                                        padding:0,
                                                                        paddingX:2,
                                                                        border:0,
                                                                        lineHeight:"28px",
                                                                        textTransform:"unset",
                                                                        borderRadius:"32px !important",
                                                                        color: colors.red[400],
                                                                        transition: "250ms",
                                                                        "&.active, &.Mui-selected, &.Mui-selected:hover": {
                                                                            background: colors.green[400],
                                                                            color: theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff",
                                                                        }
                                                                    }}
                                                                    value="true"
                                                                >
                                                                    {t('yes')}
                                                                </ToggleButton>
                                                            </ToggleButtonGroup>
                                                            {rowSaveButton}
                                                            </Box>
                                                        </Box>
                                                    );
                                                }

                                                if (field.type === "custom") {
                                                    return (
                                                        <Box
                                                            key={field.name}
                                                            sx={{
                                                                width:1,
                                                                display:"flex",
                                                                alignItems:"flex-start",
                                                                gap:2,
                                                            }}
                                                        >
                                                            <Box sx={{width:180, flexShrink:0, height:32, display:"flex", alignItems:"center"}}><Text light noBreak>{field.label}</Text></Box>
                                                            {field.render
                                                                ? field.render({
                                                                    value,
                                                                    onChange: (newValue) => updateQueryParam(field.name, newValue),
                                                                    values: queryParams,
                                                                })
                                                                : null}
                                                            {rowSaveButton}
                                                        </Box>
                                                    );
                                                }

                                                return null;
                                            })}
                                        </Box>
                                    </Collapse>
                                </ButtonBase>
                            )}
                        </Box>

                        <Box
                            sx={{
                                width:1,
                                alignItems:"center",
                                gap:0,
                                mt:1.5,
                                height:400,
                                overflow:'auto'
                            }}
                        >

                            {!uploadFileLoading && uploadResults && (
                                <Box
                                    sx={{
                                        mt:2,
                                        background: uploadResults.results.some(x => x.notImported.length > 0) ? colors["red"][100] : theme.palette.mode === "dark" ? colors["blue"][100] : colors['primary'][100],
                                        color: uploadResults.results.some(x => x.notImported.length > 0) ? colors["red"][400] : theme.palette.mode === "dark" ? colors["blue"][400] : colors['primary'][400],
                                        padding: 2,
                                        width: 1,
                                        borderRadius:3,
                                    }}
                                >
                                    <Typography fontWeight={"bold"} fontSize={17}>
                                        {t("upload_file_results_title")}: {selectedFile.name || ""}
                                    </Typography>

                                    <Box sx={{ paddingTop: 0.5, maxWidth: 500 }}>
                                        {/* Errors */}
                                        {uploadResults.errors?.length > 0 && (
                                            <>
                                            <Typography fontSize={14} fontWeight="bold" color="error">
                                                Errors:
                                            </Typography>
                                            {uploadResults.errors.map((err, idx) => (
                                                <Typography key={idx} fontSize={14}>
                                                • {err.error}
                                                </Typography>
                                            ))}
                                            </>
                                        )}

                                        {/* Results */}
                                        {uploadResults.results?.length > 0 && (
                                            <>
                                            <Typography fontSize={14} fontWeight="bold" sx={{ mt: 1 }}>
                                                {t("import_results")}:
                                            </Typography>
                                            {uploadResults.results.map((res, idx) => (
                                                <Box key={idx} sx={{ ml: 2, mt: 0.5 }}>
                                                <Typography fontSize={14}>{t("import_sheet")}: {res.sheet}</Typography>
                                                {res.created !== undefined && <Typography fontSize={14}>{t("import_created")}: {res.created}</Typography>}
                                                {res.found !== undefined && <Typography fontSize={14}>{t("import_existing")}: {res.found}</Typography>}
                                                {res.updated !== undefined && <Typography fontSize={14}>{t("import_updated")}: {res.updated}</Typography>}
                                                {res.deactivated !== undefined && <Typography fontSize={14}>{t("import_deactivated")}: {res.deactivated}</Typography>}
                                                {res.skipped !== undefined && <Typography fontSize={14}>{t("import_skipped")}: {res.skipped}</Typography>}
                    
                                                {res.notImported?.length > 0 && (
                                                    <Box sx={{ ml: 2 }}>
                                                    <Typography fontSize={14} fontWeight="bold">
                                                        {t("import_row_errors")}:
                                                    </Typography>
                                                    {res.notImported.map((ni, niIdx) => (
                                                        <Typography key={niIdx} fontSize={14}>
                                                        {t("line")} {ni.line}: {ni.name} — {ni.error}
                                                        </Typography>
                                                    ))}
                                                    </Box>
                                                )}
                                                </Box>
                                            ))}
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            )}


                            {/* A file whose columns are fixed lists them here. An import whose layout depends on a
                                setting, such as the carrier a shipping cost invoice comes from, passes no columns
                                and explains itself with a notice instead. */}
                            {props.columns?.length > 0 && (
                                <Box
                                    sx={{
                                        width:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                        borderRadius:4,
                                        p:1.5,
                                        flex:1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:1,
                                            display:'flex',
                                        }}
                                    >
                                        <Text sx={{flex:1,}} light>{t("column")}</Text>
                                        <Text light sx={{width:80}}>{t("required")}</Text>

                                    </Box>
                                    {props.columns.map((col,idx) => (
                                        <Box
                                            key={"col"+idx}
                                            sx={{
                                                width:1,
                                                display:'flex',
                                                mt:1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    flex:1,
                                                    display:"flex",
                                                    flexDirection:'column'
                                                }}
                                            >
                                                <Text>{t(col.column)}</Text>
                                                {col.columnExplainer &&
                                                    <Text light fontSize={13}>{t(col.columnExplainer)}</Text>
                                                }
                                            </Box>
                                            {col.required &&
                                                <Heading sx={{width:80,color:colors.green[400]}}>*</Heading>
                                            }

                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {props.notice && (
                                <Box
                                    sx={{
                                        width:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                        borderRadius:4,
                                        p:1.5,
                                        flex:1,
                                    }}
                                >
                                    <Text light>{props.notice}</Text>
                                </Box>
                            )}


                        </Box>

                        <Box
                            sx={{
                                width:1,
                                flexShrink:0,
                                mt:1.5,
                                display:"flex",
                                flexDirection:"column",
                                alignItems:"center",
                            }}
                        >
                            {selectedFile ?
                                <Box
                                    sx={{
                                        width:1,
                                        pl:0.5,
                                        paddingRight:0.5,
                                        height:40,
                                        borderRadius:5,
                                        // background:"rgba(65,128,246,.15)",
                                        background: colors[theme.palette.mode === "dark" ? "blue" : "primary"][100],
                                        marginTop:2,
                                        color: "rgb(65,128,246)",
                                        fontWeight:700,
                                        display:"flex",
                                        alignItems:"center",
                                        [`@media screen and (max-width: ${getBreakPoint()}px)`] : {
                                            width:"calc(100% - 56px)",
                                            marginLeft:7,
                                        }
                                    }}
                                >
                                    <ButtonBase
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setSelectedFile(null);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = "";
                                            }
                                        }}
                                        sx={{
                                            mr:2,
                                            height:32,
                                            width:32,
                                            flexShrink:0,
                                            display:"flex",
                                            alignItems:'center',
                                            justifyContent:"center",
                                            borderRadius:4,
                                            background:colors[theme.palette.mode === "dark" ? "blue" : "primary"][100],
                                            color:colors[theme.palette.mode === "dark" ? "blue" : "primary"][400],
                                            fontWeight:700
                                        }}
                                    >
                                        <DeleteOutlineOutlinedIcon fontSize="small"/>
                                    </ButtonBase>
                                    <Text
                                        noBreak
                                        variant={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        // color="rgb(65,128,246)"
                                    >
                                        {selectedFile.name}
                                    </Text>
                                    

                                    <ButtonBase
                                        disabled={uploadFileLoading}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmEndStreamModal.setOpen(true);
                                        }}
                                        // onClick={handleUpload}
                                        sx={{
                                            ml:"auto",
                                            height:32,
                                            // width:32,
                                            paddingX:1.5,
                                            background:"rgb(65,128,246)",
                                            background: colors[theme.palette.mode === "dark" ? "blue" : "primary"][400],
                                            borderRadius:4,
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                            fontWeight:700
                                        }}
                                    >
                                        {t("upload")}
                                    </ButtonBase>
                                </Box>
                            :
                            // Without an example file there is nothing to download: a carrier invoice is
                            // imported as the carrier exports it, so no template exists.
                            props.exampleFile ?
                                <ButtonBase
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if(props.exampleFile) {
                                            window.open(props.exampleFile, "_blank");
                                        }
                                    }}
                                    sx={{
                                        width:1,
                                        background:colors.green[100],
                                        height:40,
                                        px:0.5,
                                        display:'flex',
                                        alignItems:'center',
                                        borderRadius:5,
                                        gap:1.5,
                                        mt:1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:32,
                                            borderRadius:4,
                                            width:32,
                                            flexShrink:0,
                                            background:colors.green[200],
                                            display:'flex',
                                            alignItems:"center",
                                            justifyContent:'center',
                                        }}
                                    >
                                        <Excel />


                                    </Box>
                                    <Text sx={{mx:"auto"}} variant={"green"} bold>{t("download_example_file")}</Text>

                                    <Box
                                        sx={{
                                            height:32,
                                            borderRadius:4,
                                            width:32,
                                            flexShrink:0,
                                        }}
                                    >
                                    </Box>
                                </ButtonBase>
                            : null
                            }
                        </Box>

                <MConfirmation
                    open={confirmEndStreamModal.open}
                    handleClose={handleConfirmEndStreamClose}
                    title={t("confirm_import_title")}
                    content={t("confirm_import_subtitle")}
                    confirm={(event) => handleUpload(event)}
                    stopPropagation={true}
                />
                    </>
                )}
            </ButtonBase>
        </Box>

    )

}